import { isPlatformBrowser, Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Inject,
  Input,
  OnInit,
  PLATFORM_ID,
  ViewEncapsulation
} from '@angular/core';
import { IconLookup, IconProp } from '@fortawesome/fontawesome-svg-core';
import { faBluesky, faFacebook, faWhatsapp, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope, faShareAlt } from '@fortawesome/free-solid-svg-icons';

import { ENVIRONMENT } from '@environment';
import { getMovieGenres, getMovieTags, Movie } from '@usheru-hff/shared/domain';
import { TrackingService } from '@usheru-hff/sdk/tracking';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';

interface SocialLink {
  icon: IconLookup;
  iconProp: IconProp;
  platform: string;
  to: string;
}

/**
 * Component that shows links to share a movie in different social platforms.
 *
 * //TODO barbeitoalex propostal - refractor name to something like "movie-social-links".
 * //TODO barbeitoalex propostal - organice life cycle methods. Split in specific method to improve docs.
 * //TODO barbeitoalex propostal - update translate text with an observable to the current language
 * //TODO barbeitoalex propostal - subscribe to current language to update the translate pipes
 */
@Component({
  selector: 'ush-movie-tell-friends',
  templateUrl: './movie-tell-friends.component.html',
  styleUrls: ['./movie-tell-friends.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MovieTellFriendsComponent implements OnInit {
  private readonly env = inject(ENVIRONMENT);
  /**
   * @docsNotRequired
   */
  @Input()
  movie?: Movie;

  @Input()
  dropdownDesing: boolean;

  @Input()
  compacted: boolean;

  /**
   * Content of the shared message.
   */
  @Input()
  message: string;

  /**
   * Subject when shared with mail.
   */
  @Input()
  mailSubject: string;

  /**
   * Subject when shared with mail.
   */
  @Input()
  campaign: string;

  /**
   * @docsNotRequired
   */
  @Input()
  trackingCategory: string;

  /**
   * @deprecard Don't found any use
   */
  @Input()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trackingExtraProps: any;

  /**
   * @docsNotRequired
   *
   * //TODO barbeitoalex propostal - remove the "to". Don't have any use.
   */
  socialLinks: SocialLink[] = [
    {
      icon: faFacebook as IconLookup,
      iconProp: 'facebook',
      platform: 'facebook',
      to: '/'
    },
    {
      icon: faXTwitter as IconLookup,
      iconProp: 'x-twitter',
      platform: 'twitter',
      to: '/'
    },
    {
      icon: faWhatsapp as IconLookup,
      iconProp: 'whatsapp',
      platform: 'whatsapp',
      to: '/'
    },
    {
      icon: faEnvelope as IconLookup,
      iconProp: 'envelope',
      platform: 'mail',
      to: '/'
    }
  ];

  shareIcon = faShareAlt;
  private readonly showBlueSky = this.env?.showBlueSky;

  constructor(
    private trackingService: TrackingService,
    private translateService: TranslationsService,
    private location: Location,
    @Inject(PLATFORM_ID) private platformId
  ) {
    if (this.showBlueSky) {
      this.socialLinks.push({
        icon: faBluesky as IconLookup,
        iconProp: 'bluesky',
        platform: 'Bluesky',
        to: '/'
      });
    }
  }

  ngOnInit(): void {
    const translated = this.translateService.translate(
      'movie.inviteToMovie',
      'Want to see ' + this.movie?.title + ' with me?',
      { title: this.movie.title }
    );

    // DEFAULT PARAMS
    if (this.movie) {
      if (!this.trackingCategory) this.trackingCategory = 'MovieDetail';
      if (!this.message) this.message = translated;
      if (!this.mailSubject) this.mailSubject = this.movie.trTitle ?? this.movie.title;
      if (!this.campaign) this.campaign = `Sharing-%movieUrl%`;
    }
  }

  /**
   * @docsNotRequired
   * @param sl
   * @returns
   */
  onShareLink(sl: SocialLink) {
    if (!this.isBrowser || !this.movie) return;
    let message = this.message.replace('_movie_', this.movie?.title || '');
    let subject = this.mailSubject.replace('_movie_', this.movie?.title || '');
    let campaign = this.campaign.replace('%movieUrl%', this.movie?.friendlyUrl || '');

    message = encodeURI(message);
    subject = encodeURI(subject);
    campaign = encodeURI(campaign);

    const concatParams = (uri: string, params: string[]) => `${uri}${uri.includes('?') ? '&' : '?'}${params.join('&')}`;

    let uri, url, utmParams, urlEnconded, mensajeBody;
    switch (sl.platform) {
      case 'facebook':
        uri = `https://facebook.com/sharer.php?quote=${message}`;
        url = window?.location.href;
        utmParams = ['utm_source=Facebook', 'utm_medium=UserShared', `utm_campaign=${campaign}`];
        urlEnconded = encodeURIComponent(`${concatParams(url, utmParams)}`);
        return `${concatParams(uri, [`u=${urlEnconded}`])}`;
      case 'twitter':
        uri = `https://twitter.com/intent/tweet?text=${message}`;
        url = window?.location.href;
        utmParams = ['utm_source=Twitter', 'utm_medium=UserShared', `utm_campaign=${campaign}`];
        urlEnconded = encodeURIComponent(`${concatParams(url, utmParams)}`);
        return `${concatParams(uri, [`url=${urlEnconded}`])}`;
      case 'whatsapp':
        uri = `https://api.whatsapp.com/send`;
        url = window?.location.href;
        utmParams = ['utm_source=Whatsapp', 'utm_medium=UserShared', `utm_campaign=${campaign}`];
        mensajeBody = `${message}${encodeURI('\n\n')}${encodeURIComponent(concatParams(url, utmParams))}`;
        return concatParams(uri, [`text=${mensajeBody}`]);
      case 'mail':
        uri = `mailto:?subject=${subject}`;
        url = window?.location.href;
        utmParams = ['utm_source=Email', 'utm_medium=UserShared', `utm_campaign=${campaign}`];
        mensajeBody = `${message}${encodeURI('\n\n')}${encodeURIComponent(concatParams(url, utmParams))}`;
        return concatParams(uri, [`body=${mensajeBody}`]);
      default:
        return '';
    }
  }

  /**
   * @docsNotRequired
   * @param sl
   */
  onShareLinkClicked(sl: SocialLink) {
    const extraProps = {};
    if (this.movie) {
      extraProps['idMovie'] = this.movie.id;
      extraProps['movie'] = this.movie.title;
      extraProps['imdb'] = this.movie?.imdb;
      extraProps['tagsMovie'] = getMovieTags(this.movie);
      extraProps['genresMovie'] = getMovieGenres(this.movie);
    }

    this.trackingService.trackEvent({
      action: 'ShareSocial',
      properties: Object.assign(
        {
          category: this.trackingCategory,
          label: this.onShareLink(sl),
          socialPlatform: sl.platform
        },
        extraProps
      )
    });
  }

  get isBrowser() {
    return isPlatformBrowser(this.platformId);
  }
}
